import { useState } from 'react'
import { cn } from '@/lib/utils'
import { CheckMarkIcon } from '@/app/common/icons/arrows'
import Label from '@/app/common/components/Label'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/shad/ui/command'
import { Badge } from '@/shad/ui/badge'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shad/ui/tooltip'
import { CloseIcon } from '@/app/common/icons/general'

interface CategoryOption {
  value: string
  label: string
}

interface CategorySelectorProps {
  options: CategoryOption[]
  selectedCategories: string[]
  handleSelect: (value: string) => void
  removeCategory: (value: string) => void
  label?: string
  error?: string
  rootError?: string
}

export function CategorySelector({
  options,
  selectedCategories,
  handleSelect,
  removeCategory,
  label = 'Categories',
  error,
  rootError,
}: CategorySelectorProps) {
  const [search, setSearch] = useState('')

  return (
    <div>
      <Label>{label}</Label>
      <div className="space-y-2">
        <Command>
          <CommandInput
            placeholder="Search categories..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList className="static min-h-20 max-h-56 h-full border border-gray-200 rounded-md">
            <CommandEmpty>No category found.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const isSelected = selectedCategories.includes(option.value)

                return (
                  <CommandItem
                    key={option.value}
                    value={option.label}
                    className={cn('rounded-md text-gray-800 my-1 text-sm', {
                      'bg-gray-100': isSelected,
                    })}
                    onSelect={() => {
                      handleSelect(option.value)
                    }}
                  >
                    <CheckMarkIcon
                      className={cn(
                        'mr-2 text-xs',
                        isSelected ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    {option.label}
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
        </Command>

        {/* Display selected categories as badges */}
        <div className="flex flex-wrap gap-2 mt-2">
          {selectedCategories.map((categoryId) => {
            const category = options.find((opt) => opt.value === categoryId)

            if (!category) {
              return null // Skip rendering this badge if category not found
            }

            return (
              <TooltipProvider key={categoryId}>
                <Tooltip>
                  <TooltipTrigger>
                    <Badge
                      onClick={(event) => {
                        event.stopPropagation()
                        event.preventDefault()
                      }}
                      variant="secondary"
                      className="px-2 w-[6rem] flex justify-between py-1 border-gray-200"
                    >
                      <span className="truncate">{category.label}</span>
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation()
                          event.preventDefault()
                          removeCategory(categoryId)
                        }}
                      >
                        <CloseIcon className="ml-1 h-3 w-3 cursor-pointer" />
                      </button>
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{category.label}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )
          })}
        </div>
      </div>
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}

      {rootError && <p className="text-red-500 text-xs">{rootError}</p>}
    </div>
  )
}
